import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import { createMasterDataService } from '../../../api/masterData';

const LinkedDropdownField = ({ 
  field, 
  value, 
  onChange, 
  error,
  linkedConfig,
  disabled = false,
  onSelect = null, // New callback for full object selection
  isMultiSelect = false // New prop for multiselect functionality
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(isMultiSelect ? '' : (typeof value === 'string' ? value : ''));
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [selectedItems, setSelectedItems] = useState(isMultiSelect ? (Array.isArray(value) ? value : []) : []);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const isUpdatingProgrammatically = useRef(false);

  const service = linkedConfig ? createMasterDataService(linkedConfig.endpoint) : null;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch options when component mounts or linkedConfig changes
  useEffect(() => {
    if (linkedConfig && service) {
      fetchOptions();
    }
  }, [linkedConfig]);

  // Update search term when value changes (for editing existing records)
  useEffect(() => {
    if (!isMultiSelect && value && options.length > 0) {
      // If value is an ObjectId, find the corresponding option and set display name
      const selectedOption = options.find(opt => (opt._id || opt.id) === value);
      if (selectedOption) {
        const displayValue = linkedConfig.displayField || 'name';
        isUpdatingProgrammatically.current = true;
        setSearchTerm(selectedOption[displayValue]);
        setTimeout(() => {
          isUpdatingProgrammatically.current = false;
        }, 0);
      }
    }
  }, [value, options, isMultiSelect, linkedConfig]);

  // Filter options based on search term
  useEffect(() => {
    if (options.length > 0) {
      const filtered = options.filter(option => {
        const searchValue = linkedConfig.searchField || 'name';
        const optionValue = option[searchValue]?.toString().toLowerCase() || '';
        const searchTermStr = (searchTerm || '').toString().toLowerCase();
        return optionValue.includes(searchTermStr);
      });
      setFilteredOptions(filtered.slice(0, 10)); // Limit to 10 results
    }
  }, [searchTerm, options, linkedConfig]);

  const fetchOptions = async () => {
    if (!service) return;
    
    setLoading(true);
    try {
      const result = await service.getAll({
        limit: 100,
        isActive: true
      });
      setOptions(result.records || []);
    } catch (error) {
      console.error(`Error fetching ${linkedConfig.displayName} options:`, error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    
    // Don't process changes if we're updating programmatically
    if (isUpdatingProgrammatically.current) {
      return;
    }
    
    setSearchTerm(newValue);
    
    // For multiselect, don't update the form value when typing
    // For single select, only update when user actually selects an option
    if (isMultiSelect) {
      // For multiselect, the typing is just for search, don't update form value
    } else {
      // For single select, only clear the value if user clears the search
      // but don't send intermediate typed values
      if (newValue === '') {
        onChange(''); // Clear the value if user clears the input
      }
    }
    
    if (newValue.length >= 2) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  const handleOptionSelect = (option) => {
    if (isMultiSelect) {
      const optionId = option._id || option.id;
      const newSelectedItems = [...selectedItems];
      const existingIndex = newSelectedItems.findIndex(id => id === optionId);
      
      if (existingIndex === -1) {
        // Add to selection
        newSelectedItems.push(optionId);
      } else {
        // Remove from selection
        newSelectedItems.splice(existingIndex, 1);
      }
      
      setSelectedItems(newSelectedItems);
      onChange(newSelectedItems);
      setSearchTerm(''); // Clear search after selection
      
      // Keep dropdown open for multiselect
      setIsOpen(true);
    } else {
      const displayValue = linkedConfig.displayField || 'name';
      const selectedValue = option[displayValue];
      const objectId = option._id || option.id;
      console.log('LinkedDropdownField: Sending ObjectId:', objectId, 'for option:', option);
      
      // Set flag to prevent input change handler from firing
      isUpdatingProgrammatically.current = true;
      setSearchTerm(selectedValue);
      setTimeout(() => {
        isUpdatingProgrammatically.current = false;
      }, 0);
      
      onChange(objectId); // Send ObjectId instead of display name
      setIsOpen(false);
      inputRef.current?.blur();
    }
    
    // If onSelect callback is provided, pass the full object for auto-population
    if (onSelect) {
      onSelect(option);
    }
  };

  const handleRemoveItem = (itemId) => {
    const newSelectedItems = selectedItems.filter(id => id !== itemId);
    setSelectedItems(newSelectedItems);
    onChange(newSelectedItems);
  };

  const getSelectedItemsDisplay = () => {
    if (!isMultiSelect || selectedItems.length === 0) return [];
    
    return selectedItems.map(id => {
      const option = options.find(opt => (opt._id || opt.id) === id);
      return option ? {
        id: id,
        name: option[linkedConfig.displayField || 'name']
      } : null;
    }).filter(Boolean);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIsOpen(true);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    } else if (e.key === 'Enter') {
      e.preventDefault(); // Prevent form submission on Enter
      // If dropdown is open and there are filtered options, select the first one
      if (isOpen && filteredOptions.length > 0) {
        handleOptionSelect(filteredOptions[0]);
      }
    }
  };

  // If no linkedConfig, render as regular input
  if (!linkedConfig) {
    return (
      <div className="space-y-1">
        <label className="block text-sm font-medium text-gray-700">
          {field.label}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <input
          type={field.type || 'text'}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500 ${
            error ? 'border-red-500' : ''
          }`}
          placeholder={`Enter ${field.label.toLowerCase()}`}
        />
        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-gray-700">
        {field.label}
        {field.required && <span className="text-red-500 ml-1">*</span>}
        <span className="text-xs text-gray-500 ml-1">
          (Type 2+ letters to search)
        </span>
      </label>
      
      <div className="relative" ref={dropdownRef}>
        {/* Display selected items for multiselect */}
        {isMultiSelect && (
          <div className="mb-2">
            {getSelectedItemsDisplay().map((item) => (
              <span
                key={item.id}
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-1 mb-1"
              >
                {item.name}
                <button
                  type="button"
                  onClick={() => handleRemoveItem(item.id)}
                  className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full bg-blue-200 text-blue-600 hover:bg-blue-300"
                >
                  <Icon name="X" size={10} />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (isMultiSelect || (searchTerm || '').toString().length >= 2) {
                setIsOpen(true);
              }
            }}
            disabled={disabled}
            className={`w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500 ${
              error ? 'border-red-500' : ''
            }`}
            placeholder={isMultiSelect 
              ? `Search and add ${linkedConfig.displayName.toLowerCase()}...`
              : `Type to search ${linkedConfig.displayName.toLowerCase()}`
            }
          />
          
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            {loading ? (
              <Icon name="Loader2" size={16} className="text-gray-400 animate-spin" />
            ) : (
              <Icon 
                name={isOpen ? "ChevronUp" : "ChevronDown"} 
                size={16} 
                className="text-gray-400" 
              />
            )}
          </div>
        </div>

        {/* Dropdown Options */}
        {isOpen && (isMultiSelect || (searchTerm || '').toString().length >= 2) && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
            {loading ? (
              <div className="px-3 py-2 text-center text-gray-500">
                <Icon name="Loader2" size={16} className="animate-spin inline mr-2" />
                Loading...
              </div>
            ) : filteredOptions.length > 0 ? (
              <>
                {filteredOptions.map((option, index) => {
                  const displayValue = linkedConfig.displayField || 'name';
                  const secondaryField = linkedConfig.secondaryField;
                  const tertiaryField = linkedConfig.tertiaryField;
                  const optionId = option._id || option.id;
                  const isSelected = isMultiSelect && selectedItems.includes(optionId);
                  
                  return (
                    <button
                      type="button"
                      key={option.id || index}
                      onClick={() => handleOptionSelect(option)}
                      className={`w-full text-left px-3 py-2 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none transition-colors duration-150 ${
                        isSelected ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 flex items-center">
                          {isMultiSelect && (
                            <div className="mr-2">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {}} // Handled by button click
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                          )}
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">
                              {option[displayValue]}
                            </div>
                            {(secondaryField || tertiaryField) && (
                              <div className="text-sm text-gray-500 flex items-center space-x-1">
                                {secondaryField && option[secondaryField] && (
                                  <span>{option[secondaryField]}</span>
                                )}
                                {secondaryField && tertiaryField && option[secondaryField] && option[tertiaryField] && (
                                  <span>•</span>
                                )}
                                {tertiaryField && option[tertiaryField] && (
                                  <span>{option[tertiaryField]}</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        {linkedConfig.showCode && option.code && (
                          <div className="text-xs text-gray-400 font-mono ml-2">
                            {option.code}
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </>
            ) : (
              <div className="px-3 py-2 text-center text-gray-500">
                No results found
              </div>
            )}
          </div>
        )}
      </div>
      
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
};

export default LinkedDropdownField;
